<!--
  @Shop  Page "Handeln" of the play module (player view): items offered by merchants, found by the fellowship or
         offered by companions. Items are marked for trade with the bookmark button (= possession with gained NULL).
-->

<script module>
		import {user, game, view, send_data} from "../client.svelte.js";
		import {protoShopitems} from "../core.js";

		/** state shared with panels/TradePanel.svelte and the master page (panels/MasterTradePanel.svelte, play/TradeDesk.svelte) **/
		export const shop = $state({source: 'merchant'})		// 'merchant', 'found', 'companion' or null (= all sources)

		export const shopSources = {
				merchant:  {label: "Händler", srcLabel: "Händler"},
				found:     {label: "Fundstücke", srcLabel: "Fundstück der Gemeinschaft"},
				companion: {label: "Gefährte", srcLabel: "Gefährte"} }

		/** possessions of the hero, which are marked for trade: new items (gained NULL) or further pieces (delta) **/
		export function marked_possessions(character) {
				return (character?.possessions ?? [])
						.map(possID => user.possessions.get(possID))
						.filter(poss => poss && (poss.gained === null || poss.delta > 0))
		}

		/** all offers (item, source, price in silver) of the merchant and the fellowship **/
		export function collect_offers() {
				const merchantOffers = protoShopitems.map(item => ({item, source: 'merchant', price: item.value ?? 0}))
				const heldItemIDs = new Set(user.possessions.values().filter(poss => poss.gained !== null).map(poss => poss.ITEM))
				const foundOffers = (game.obtainables ?? [])
						.map(itemID => user.gearitems.get(itemID))
						.filter(item => item && !heldItemIDs.has(item.ID))
						.map(item => ({item, source: 'found', price: 0}))
				// ToDo: offers of companions need a trade flag and an asking price on their possessions
				return merchantOffers.concat(foundOffers)
		}

		/** marked possessions of the hero with item, source and price (items derived from shop items count as merchant items) **/
		export function marked_entries(character) {
				const shopItems = new Map(protoShopitems.map(item => [item.ID, item]))
				return marked_possessions(character)
						.map(poss => ({poss, item: shopItems.get(poss.ITEM) ?? user.gearitems.get(poss.ITEM)}))
						.filter(entry => entry.item)
						.map(entry => (game.obtainables ?? []).includes(entry.item.ID)
								? {...entry, source: 'found', price: 0}
								: {...entry, source: 'merchant', price: entry.item.value ?? 0})
		}

		export function toggle_mark(item, possession) {
				if (possession)
						send_data(`possession/${possession.ID}`, {}, 'DELETE')
				else
						send_data('possession/_', {CHAR: view.character.ID, ITEM: item.ID, amount: 1, gained: null}, 'POST')
		}
</script>

<script>
		import {gearTypes, gear_type, money_label, perksToString} from "../core.js";
		import ModuleTabs from "../elements/ModuleTabs.svelte";

		let search = $state("")
		let sorting = $state('name')
		let gearTab = $state('all')

		const sorters = {
				name:   (a, b) => a.item.name.localeCompare(b.item.name, 'de'),
				price:  (a, b) => a.price - b.price,
				grade:  (a, b) => (+b.item.grade || 0) - (+a.item.grade || 0),
				weight: (a, b) => (a.item.weight ?? 0) - (b.item.weight ?? 0) }

		let cash = $derived(
				(user.possessions.get(`${view.character?.ID}$gold`)?.amount ?? 0) * 100 +
				(user.possessions.get(`${view.character?.ID}$silver`)?.amount ?? 0) )
		let marks = $derived(new Map(marked_possessions(view.character).map(poss => [poss.ITEM, poss])))

		/** offers filtered by source and search (basis for the tab counts) **/
		let offers = $derived.by(() => {
				const query = search.trim().toLowerCase()
				return collect_offers()
						.filter(offer => !shop.source || offer.source === shop.source)
						.filter(offer => !query || [offer.item.name, offer.item.descript, offer.item.specials].join(" ").toLowerCase().includes(query))
		})
		let shown = $derived(offers.filter(offer => gearTab === 'all' || gear_type(offer.item) === gearTab).sort(sorters[sorting]))

		let tabs = $derived([{id: 'all', label: "Alle", icon: 'table-cells', count: offers.length}].concat(
				Object.entries(gearTypes).map(([type, gearType]) =>
						({id: type, label: gearType.label, icon: gearType.icon, count: offers.filter(offer => gear_type(offer.item) === type).length}) ) ))

		function weight_label(weight) {
				return (weight) ? `${weight.toLocaleString('de-DE', {maximumFractionDigits: 1})} kg` : "–"
		}

		function show_item(e) {
				if (e.target.closest('button'))  return
				view.show_dialog('gearitem', {item: this, mode: 'view'})
		}
</script>


<!-------------------------------- HTML -------------------------------------->

<section class="shop" class:ismaster={user.isMaster}>

	<div class="head">
		<div>
			<p class="kicker">Markt & Gemeinschaft</p>
			<h1>Handeln</h1>
		</div>
		<div class="controls">
			<div class="ctrl">
				<label for="shop-search">Suchen</label>
				<input id="shop-search" type="search" bind:value={search} placeholder="Name oder Besonderheit">
			</div>
			<div class="ctrl">
				<label for="shop-sort">Sortieren</label>
				<select id="shop-sort" bind:value={sorting}>
					<option value="name">Name</option>
					<option value="price">Preis</option>
					<option value="grade">Stufe</option>
					<option value="weight">Gewicht</option>
				</select>
			</div>
		</div>
	</div>

	<div class="infobar">
		<span>{offers.length} Gegenstände verfügbar</span>
		<span class="hint">Mit dem Lesezeichen merkst du einen Gegenstand zum Kauf oder Tausch vor.</span>
	</div>

	<ModuleTabs {tabs} selected={gearTab} onselect={(tab) => gearTab = tab} />

	{#if shown.length === 0}
		<div class="empty">
			{(shop.source === 'companion') ? "Deine Gefährten bieten zurzeit nichts zum Tausch an." : "Keine Gegenstände passen zu diesem Filter."}
		</div>
	{/if}

	<ul class="items">
		{#each shown as offer (`${offer.source}:${offer.item.ID}`)}
			{@render itemCard(offer)}
		{/each}
	</ul>

</section>

{#snippet itemCard({item, source, price})}
	{@const type = gear_type(item)}
	{@const possession = marks.get(item.ID)}
	<!-- svelte-ignore a11y_click_events_have_key_events, a11y_no_noninteractive_element_interactions -->
	<li class="item" class:marked={possession} onclick={show_item.bind(item)}>
		{#if item.imgpath}
			<img class="tile" src={item.imgpath} alt={item.name}>
		{:else}
			<div class="tile noimg">
				<i class="fa icon-{gearTypes[type].icon}"></i>
				<span>kein Bild</span>
			</div>
		{/if}

		<div class="info">
			<div class="title">
				<h3>{item.name}</h3>
				<span class="tag neutral">{gearTypes[type].name}</span>
			</div>
			{#if item.descript}<p class="descript">{item.descript}</p>{/if}
			<div class="source {source}"><span class="dot"></span>{shopSources[source].srcLabel}</div>
		</div>

		<dl class="attribs">
			<dt>Stufe</dt>
			<dt>Gewicht</dt>
			<dt>Besonderheiten</dt>
			<dd class="grade">{+item.grade || "–"}</dd>
			<dd>{weight_label(item.weight)}</dd>
			<dd class="specials" title={perksToString(item.perks ?? {})}>{item.specials || "–"}</dd>
		</dl>

		<div class="buy">
			<div class="price">
				<span class="kicker">{(source === 'found') ? "Fund" : (source === 'companion') ? "Wunschpreis" : "Preis"}</span>
				<span class="value" class:unaffordable={price > cash}>{(source === 'found') ? "frei" : money_label(price)}</span>
			</div>
			<button class="mark" aria-pressed={!!possession} onclick={() => toggle_mark(item, possession)}
			        aria-label="{(possession) ? 'Von der Merkliste nehmen' : 'Vormerken'}: {item.name}"
			        title={(possession) ? "Von der Merkliste nehmen" : "Zum Kauf oder Tausch vormerken"}>
				<svg width="20" height="20" viewBox="0 0 24 24" fill={(possession) ? 'currentColor' : 'none'} stroke="currentColor"
				     stroke-width="1.8" stroke-linejoin="round" aria-hidden="true"><path d="m19 21-7-4-7 4V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"></path></svg>
			</button>
		</div>
	</li>
{/snippet}


<!-------------------------------- STYLE -------------------------------------->

<style lang="sass">
	@use "../colors" as colors

	.shop
		grid-area: main
		display: flex
		flex-direction: column
		gap: 16px
		min-width: 0
		max-height: calc(100vh - 24px)
		margin-top: 12px
		padding: 20px
		overflow-y: auto
		scrollbar-width: thin
		border: 1px solid colors.$grey-20
		border-radius: 10px
		background: #1b1a1a

	.kicker
		margin: 0
		font-size: 12px
		letter-spacing: .08em
		text-transform: uppercase
		color: colors.$primary

	h1
		margin: 0
		font-family: var(--font-heading, "EB Garamond", Georgia, serif)
		font-size: 34px
		line-height: 1.1

	/* — header row with search and sorting (right padding keeps clear of the help button of the player) — */
	.head
		display: flex
		flex-wrap: wrap
		align-items: flex-end
		justify-content: space-between
		gap: 12px
		padding-right: 110px

		.ismaster &
			padding-right: 0

	.controls
		display: flex
		flex-wrap: wrap
		align-items: flex-end
		gap: 10px

	.ctrl
		display: flex
		flex-direction: column
		gap: 3px

		label
			font-size: 12px
			color: colors.$grey-60

		input, select
			height: 44px
			margin: 0
			padding: 0 12px
			border: 1px solid colors.$grey-30
			border-radius: 6px
			background: colors.$black
			color: colors.$white
			font-size: 15px
			font-family: inherit

			&:focus-visible
				outline: 2px solid colors.$primary
				outline-offset: 2px

		input
			width: 240px
			max-width: 100%

	.infobar
		display: flex
		flex-wrap: wrap
		justify-content: space-between
		align-items: baseline
		gap: 8px
		padding-bottom: 10px
		border-bottom: 1px solid colors.$grey-20
		font-size: 14px
		color: colors.$grey-85

		.hint
			font-size: 13px
			color: colors.$grey-60

	/* the tabs stretch over the padding of the page */
	.shop > :global(.tabs)
		flex: none
		margin: -16px -20px 0

	.empty
		padding: 48px 16px
		border: 1px dashed colors.$grey-30
		border-radius: 10px
		text-align: center
		color: colors.$grey-60

	/* — item cards — */
	.items
		display: flex
		flex-direction: column
		gap: 10px
		margin: 0
		padding: 0
		list-style: none

	.item
		display: flex
		flex-wrap: wrap
		align-items: center
		gap: 16px
		padding: 14px
		border: 1px solid colors.$grey-30
		border-radius: 10px
		background: colors.$grey-15
		cursor: pointer

		&:hover
			border-color: colors.$grey-50

		&.marked
			border-color: colors.$primary-100
			background: #0b2a2a

	.tile
		flex: none
		width: 96px
		height: 96px
		border-radius: 8px
		object-fit: cover

		&.noimg
			display: flex
			flex-direction: column
			align-items: center
			justify-content: center
			gap: 6px
			border: 1px dashed colors.$grey-30
			color: colors.$grey-50

			.fa
				font-size: 28px

			span
				font-size: 11px
				color: colors.$grey-60

	.info
		flex: 1 1 220px
		min-width: 0
		display: flex
		flex-direction: column
		gap: 6px

	.title
		display: flex
		flex-wrap: wrap
		align-items: center
		gap: 8px

		h3
			margin: 0
			font-family: var(--font-heading, "EB Garamond", Georgia, serif)
			font-size: 20px
			line-height: 1.15

	/* same look as .tag.neutral in blocks.sass (which is scoped to the prepare and narrate modules) */
	.tag
		flex: none
		padding: 1px 9px
		border-radius: 20px
		font-size: 12px
		white-space: nowrap
		border: 1px solid colors.$grey-30
		color: colors.$grey-85

		&.neutral
			background: colors.$grey-20

	.descript
		margin: 0
		font-size: 14px
		color: colors.$grey-85

	.source
		display: flex
		align-items: center
		gap: 6px
		font-size: 13px
		color: colors.$grey-60

		.dot
			width: 8px
			height: 8px
			border-radius: 50%
			background: colors.$grey-50

		&.found .dot
			background: colors.$primary-100

		&.companion
			color: colors.$highlight-yellow-100

			.dot
				background: colors.$highlight-yellow-040-black
				border: 1px solid colors.$highlight-yellow-100

	.attribs
		flex: 1 1 260px
		min-width: 0
		margin: 0
		display: grid
		grid-template-columns: 52px 76px minmax(0, 1fr)
		gap: 2px 16px
		align-items: baseline

		dt
			font-size: 12px
			letter-spacing: .06em
			text-transform: uppercase
			color: colors.$grey-60

		dd
			margin: 0
			font-size: 15px

		.grade
			font-family: var(--font-heading, "EB Garamond", Georgia, serif)
			font-size: 20px
			font-weight: bold

		.specials
			font-size: 14px
			color: colors.$grey-85

	.buy
		flex: none
		display: flex
		align-items: center
		gap: 14px
		margin-left: auto

	.price
		display: flex
		flex-direction: column
		align-items: flex-end
		line-height: 1.2

		.kicker
			letter-spacing: .06em
			color: colors.$grey-60

		.value
			font-family: var(--font-heading, "EB Garamond", Georgia, serif)
			font-size: 20px
			font-weight: bold
			white-space: nowrap

			&.unaffordable
				color: #e05252

	.mark
		width: 44px
		height: 44px
		display: grid
		place-items: center
		margin: 0
		padding: 0
		border: 1px solid colors.$grey-50
		border-radius: 8px
		background: transparent
		color: colors.$grey-85
		cursor: pointer

		&:hover
			color: colors.$white
			border-color: colors.$primary-100

		&[aria-pressed="true"]
			border-color: colors.$primary-100
			background: colors.$primary-100
			color: colors.$white

		&:focus-visible
			outline: 2px solid colors.$primary
			outline-offset: 2px
</style>
